import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";
import { CellContext, ColumnDef } from "@tanstack/react-table";

export interface SampleRow {
  id: number;
  rowIndex: number;
  rowNumber: number;
  query: string;
  formattedAddress: string;
  matchQuality: string;
  matchScore: string;
  matchType: string;
  matchDistance: number;
  matchMethod: string;
  longitude: number;
  latitude: number;
}

const cellWithTooltip = (fieldName: keyof SampleRow) => ({
  cell: ({ row }: CellContext<SampleRow, unknown>) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <p className="truncate text-sm">{row.getValue(fieldName) as string}</p>
      </TooltipTrigger>
      <TooltipContent>{row.getValue(fieldName) as string}</TooltipContent>
    </Tooltip>
  ),
});

export const extractRowData = (
  sampleResponse: Record<string, unknown>,
): SampleRow[] => {
  const response = sampleResponse as {
    rowIndexes: number[];
    queries: string[];
    featureCollection: {
      features: Array<{
        properties: { formattedAddress: string };
        matchQuality: string;
        matchScore: string;
        matchType: string;
        matchDistance: number;
        matchMethod: string;
        geometry: { coordinates: [number, number] };
      } | null>;
    };
  };
  var rows: SampleRow[] = [];

  for (var i = 0; i < response.rowIndexes.length; i++) {
    const feature = response.featureCollection.features[i];

    const hasResult = feature !== undefined && feature !== null;

    rows.push({
      id: response.rowIndexes[i],
      rowIndex: response.rowIndexes[i],
      rowNumber: response.rowIndexes[i] + 1,
      query: response.queries[i],
      formattedAddress: hasResult
        ? feature.properties.formattedAddress
        : "NO ADDRESS MATCHED THE QUERY",
      matchQuality: hasResult ? feature.matchQuality : "",
      matchScore: hasResult ? feature.matchScore : "",
      matchType: hasResult ? feature.matchType : "",
      matchDistance: hasResult ? feature.matchDistance : 0,
      matchMethod: hasResult ? feature.matchMethod : "",
      longitude: hasResult ? feature.geometry.coordinates[0] : 0,
      latitude: hasResult ? feature.geometry.coordinates[1] : 0,
    });
  }

  return rows;
};

export const defineColumns = (jobType: string) => {
  const columns: ColumnDef<SampleRow>[] = [
    { size: 100, accessorKey: "rowNumber", header: "Row Number" },
    {
      accessorKey: "query",
      header: "Query",
      ...cellWithTooltip("query"),
    },
    {
      accessorKey: "formattedAddress",
      header: "Formatted Address",
      ...cellWithTooltip("formattedAddress"),
    },
  ];

  if (jobType == "addressGeocoder") {
    columns.push(
      { size: 100, accessorKey: "matchQuality", header: "Match Quality" },
      { size: 100, accessorKey: "matchScore", header: "Match Score" },
    );
  } else if (jobType == "reverseGeocoder") {
    columns.push(
      { size: 200, accessorKey: "matchType", header: "Match Type" },
      { size: 130, accessorKey: "matchDistance", header: "Match Distance" },
      { size: 200, accessorKey: "matchMethod", header: "Match Method" },
    );
  } else {
    console.error("Unknown batch job type");
  }

  return columns;
};
